import {useEffect, useRef, useState, type MouseEvent} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Drawer,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';

export interface LastTaxDrawerProps {
    open: boolean;
    /** Preferred Paper width (A4-based, may shrink on small screens). */
    width: number;
    fileName?: string | null;
    previewUrl?: string | null;
    uploading?: boolean;
    onClose: () => void;
    onReplace: () => void;
    /** Report rendered Paper width (0 when closed), same role as RepaymentScheduleDrawer.onWidthChange. */
    onWidthChange?: (width: number) => void;
}

/** Mirror CascadeSelection lease-agreement PDF embed styles (scrollable, non-selectable). */
const leasePdfEmbedSx = {
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
} as const;

export function LastTaxDrawer({
    open,
    width,
    fileName,
    previewUrl,
    uploading,
    onClose,
    onReplace,
    onWidthChange,
}: LastTaxDrawerProps) {
    const paperRef = useRef<HTMLDivElement | null>(null);
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const [bodyHeight, setBodyHeight] = useState(0);

    // Align with RepaymentScheduleDrawer: measure Paper and notify parent for Dialog shift
    useEffect(() => {
        if (!onWidthChange) return;
        if (!open) {
            onWidthChange(0);
            return;
        }
        const measure = () => {
            const el = paperRef.current;
            if (!el) return;
            onWidthChange(el.getBoundingClientRect().width);
        };
        measure();
        const t = setTimeout(measure, 250);
        let ro: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined' && paperRef.current) {
            ro = new ResizeObserver(measure);
            ro.observe(paperRef.current);
        }
        window.addEventListener('resize', measure);
        return () => {
            clearTimeout(t);
            if (ro) ro.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [open, width, onWidthChange]);

    // Dynamically fit preview body to Paper remainder so outer never needs a scrollbar
    useEffect(() => {
        if (!open) {
            setBodyHeight(0);
            return;
        }
        const measureBody = () => {
            const el = bodyRef.current;
            if (!el) return;
            const style = window.getComputedStyle(el);
            const padY =
                (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
            // Content box only � iframe must not exceed this or outer scrollbar appears
            const h = Math.floor(el.clientHeight - padY);
            if (h > 0) setBodyHeight(h);
        };
        measureBody();
        const t = setTimeout(measureBody, 250);
        let ro: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined' && bodyRef.current) {
            ro = new ResizeObserver(measureBody);
            ro.observe(bodyRef.current);
        }
        window.addEventListener('resize', measureBody);
        return () => {
            clearTimeout(t);
            if (ro) ro.disconnect();
            window.removeEventListener('resize', measureBody);
        };
    }, [open, width, previewUrl]);

    const stopBubble = (e: MouseEvent) => {
        e.stopPropagation();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={(theme) => ({
                zIndex: theme.zIndex.modal + 2,
                position: 'relative',
            })}
            ModalProps={{
                keepMounted: true,
                hideBackdrop: true,
                disableEnforceFocus: true,
                disableScrollLock: true,
            }}
            PaperProps={{
                ref: paperRef,
                onMouseDown: stopBubble,
                onClick: stopBubble,
                sx: {
                    width,
                    maxWidth: '100vw',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    overflow: 'hidden',
                    ...leasePdfEmbedSx,
                    WebkitTouchCallout: 'none',
                    '& ::selection': {background: 'transparent'},
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    minHeight: 48,
                    flexShrink: 0,
                }}
            >
                <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography variant="subtitle2" fontWeight={700} noWrap>
                        Last Year Tax Report
                    </Typography>
                    {fileName ? (
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {fileName}
                        </Typography>
                    ) : null}
                </Box>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                        uploading
                            ? <CircularProgress size={14} />
                            : <UploadOutlinedIcon fontSize="small" />
                    }
                    disabled={!!uploading}
                    onClick={onReplace}
                >
                    Replace
                </Button>
                <IconButton
                    size="small"
                    aria-label="close last year tax report preview"
                    onClick={onClose}
                    disabled={!!uploading}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            {/* Outer: overflow hidden + dynamic height � only PDF iframe scrolls */}
            <Box
                ref={bodyRef}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'grey.100',
                    p: 1,
                    boxSizing: 'border-box',
                    ...leasePdfEmbedSx,
                }}
            >
                {previewUrl ? (
                    <Box
                        component="iframe"
                        src={`${previewUrl}#toolbar=0`}
                        title="Last Year Tax Report PDF preview"
                        sx={{
                            width: '100%',
                            height: bodyHeight > 0 ? bodyHeight : '100%',
                            minHeight: 0,
                            maxHeight: '100%',
                            border: 'none',
                            display: 'block',
                            ...leasePdfEmbedSx,
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'text.secondary',
                            px: 2,
                        }}
                    >
                        {uploading ? 'Uploading�' : 'No preview available.'}
                    </Box>
                )}
            </Box>
        </Drawer>
    );
}
